import {
  Box,
  ButtonProps,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";

import Actions from "../../components/common/Actions";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import SafeHtml from "../../components/SafeHtml";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import SearchForm from "../../components/common/SearchForm";
import DateFilterButtons from "../../components/DateFilterButtons";

import {
  exportNotifications,
  fetchNotificationById,
  resendNotification,
  INotification,
  NotificationParams,
  NotificationStatus,
  useNotificationsQuery,
  EVENT_TYPE_OPTIONS,
  STATUS_OPTIONS,
  NON_RESENDABLE_EVENT_TYPES,
  EmailEventType,
} from "../../services/NotificationsService";
import { ExportAttachmentApi } from "../../services/ExportService";

import { useQueryClient } from "@tanstack/react-query";
import { IField } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const NotificationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [filters, setFilters] = useState<NotificationParams>({});
  const [appliedFilters, setAppliedFilters] = useState<NotificationParams>({
    search: "",
  });
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const dialogRef = useRef<NotificationDialogRef>(null);

  const handleOpenDialog = (mode: "view", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setSearchTrigger((prev) => prev + 1);
    setAppliedFilters({
      search: appliedFilters.search,
      fromDate: fromDate,
      toDate: toDate,
      eventType: filters.eventType,
      status: filters.status,
    });
  };

  const handleDateFilterSelect = (option: string) => {
    if (option) {
      const [startStr, endStr] = option.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getUTCFullYear();
    const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(today.getUTCDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(today.getUTCDate() + 1);
    const yyyy2 = tomorrow.getUTCFullYear();
    const mm2 = String(tomorrow.getUTCMonth() + 1).padStart(2, "0");
    const dd2 = String(tomorrow.getUTCDate()).padStart(2, "0");
    const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;

    const dateRangeStr = `${todayStr}_${tomorrowStr}`;
    handleDateFilterSelect(dateRangeStr);

    // Also apply the dates to the filters for the API call
    const [startStr, endStr] = dateRangeStr.split("_");
    const initialFromDate = new Date(startStr);
    const initialToDate = new Date(endStr);

    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: initialFromDate,
      toDate: initialToDate,
    }));
  }, []);

  const actions = Actions({
    onView: {
      handler: async (id: string | number) => {
        handleOpenDialog("view", String(id));
      },
      isDisabled: !hasJwtPermission("GET_NOTIFICATION"),
    },
  });

  const getStatusIcon = (notification: INotification) => {
    if (notification.status === NotificationStatus.SENDING) {
      return <CircularProgress size={24} sx={{ color: COLORS.GOLD }} />;
    }
    if (notification.status === NotificationStatus.RETRYING) {
      return <SyncIcon sx={{ color: COLORS.WarningOrange, fontSize: 24 }} />;
    }
    if (notification.status === NotificationStatus.PARTIALLY_SENT) {
      return (
        <WarningAmberIcon sx={{ color: COLORS.WarningOrange, fontSize: 24 }} />
      );
    }
    if (notification.isEmailSent) {
      return (
        <CheckCircleIcon sx={{ color: COLORS.successGreen, fontSize: 24 }} />
      );
    }
    return <CancelIcon sx={{ color: COLORS.errorRed, fontSize: 24 }} />;
  };

  const fields: IField<INotification>[] = [
    {
      key: "isEmailSent",
      header: "",
      transform: (t) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            minWidth: "50px",
            width: "50px",
          }}
        >
          {getStatusIcon(t)}
        </Box>
      ),
    },
    {
      key: "eventType",
      header: "Event Type",
      transform: (t) => {
        const option = EVENT_TYPE_OPTIONS.find(
          (opt) => opt.value === t.eventType,
        );
        return option?.label || t.eventType;
      },
    },
    {
      key: "status",
      header: "Notification Status",
      transform: (t) => {
        const option = STATUS_OPTIONS.find((opt) => opt.value === t.status);
        return option?.label || t.status;
      },
    },
    { key: "fromEmail", header: "From Email" },
    { key: "sendToEmail", header: "To Email" },
    { key: "ccEmail", header: "CC" },
    { key: "subject", header: "Subject" },
    { key: "failedTries", header: "Failed Tries" },
    {
      key: "sentDate",
      header: "Sent Date",
      transform: (t) => {
        if (!t.sentDate) return "-";
        const date = new Date(t.sentDate);
        return date.toLocaleString();
      },
    },
  ];

  const autoCompletes = [
    {
      label: "Event Type",
      value: filters.eventType || "",
      options: EVENT_TYPE_OPTIONS,
      placeholder: "Select Event Type",
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          eventType: value ? String(value) : undefined,
        }));
      },
    },
    {
      label: "Status",
      value: filters.status || "",
      options: STATUS_OPTIONS,
      placeholder: "Select Status",
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          status: value ? String(value) : undefined,
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_NOTIFICATIONS",
    },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Email Notifications"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <Box display="flex" flexDirection="column" gap={2} width="100%">
                <DateFilterButtons onSelect={handleDateFilterSelect} />
                <CustomDateRangePicker
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                />
              </Box>
            </Box>
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Notifications List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportNotifications(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_NOTIFICATIONS_CSV",
            },
          ]}
        />
        <CustomTable<INotification>
          filters={{ ...appliedFilters, _trigger: searchTrigger } as any}
          fields={fields}
          fetchFunc={useNotificationsQuery as any}
          actions={actions}
          sortableFields={["sentDate", "eventType", "status"]}
        />
      </Box>
      <NotificationDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries();
        }}
      />
    </Box>
  );
};

export interface NotificationDialogRef {
  openDialog: (mode: "view", id?: string) => void;
}

interface NotificationDialogProps {
  onRefresh: () => void;
}

const NotificationDialog = forwardRef<
  NotificationDialogRef,
  NotificationDialogProps
>(({ onRefresh }, ref) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<Partial<INotification>>({});
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDetails, setDownloadingDetails] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: async (mode: "view", id?: string) => {
      if (id) {
        setLoading(true);
        setDialogOpen(true);
        try {
          const notification = await fetchNotificationById(id);
          setDialogData(notification);
        } catch (error: any) {
          ToastNotification.error(
            error.message || "Failed to load notification details.",
          );
          setDialogOpen(false);
        } finally {
          setLoading(false);
        }
      }
    },
  }));

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogData({});
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const canResend =
    dialogData.eventType &&
    !NON_RESENDABLE_EVENT_TYPES.includes(
      dialogData.eventType as EmailEventType,
    );

  const handleResend = async () => {
    if (!dialogData.id) return;

    setResending(true);
    try {
      await resendNotification(dialogData.id);
      ToastNotification.success("Email queued for resend successfully!");
      closeDialog();
      onRefresh();
    } catch (error: any) {
      ToastNotification.error(
        error.message || "Failed to resend notification.",
      );
    } finally {
      setResending(false);
    }
  };

  const handleDownloadAttachment = async () => {
    if (!dialogData.attachmentId) return;

    setDownloading(true);
    try {
      await ExportAttachmentApi(dialogData.attachmentId, "notification");
      ToastNotification.success("Attachment downloaded successfully!");
    } catch (error: any) {
      ToastNotification.error(
        error.message || "Failed to download attachment.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDetailsAttachment = async () => {
    if (!dialogData.detailsAttachmentId) return;

    setDownloadingDetails(true);
    try {
      await ExportAttachmentApi(dialogData.detailsAttachmentId, "notification");
      ToastNotification.success("Details attachment downloaded successfully!");
    } catch (error: any) {
      ToastNotification.error(
        error.message || "Failed to download details attachment.",
      );
    } finally {
      setDownloadingDetails(false);
    }
  };

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={closeDialog}
      mode="Accounts"
      title="Notification Details"
      dialogSx={{ minWidth: "55vw" }}
      fields={
        loading
          ? [
              {
                type: "custom",
                component: (
                  <Box sx={{ textAlign: "center", padding: 4 }}>Loading...</Box>
                ),
              },
            ]
          : [
              {
                type: "custom",
                component: (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <CustomLabel text="Event Type" fontWeight={600} />
                        <Box sx={{ color: COLORS.typographyColor }}>
                          {EVENT_TYPE_OPTIONS.find(
                            (opt) => opt.value === dialogData.eventType,
                          )?.label ||
                            dialogData.eventType ||
                            "-"}
                        </Box>
                      </Box>
                      <Box>
                        <CustomLabel text="Status" fontWeight={600} />
                        {dialogData.status === NotificationStatus.SENDING ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CircularProgress
                              size={18}
                              sx={{ color: COLORS.GOLD }}
                            />
                            <span style={{ color: COLORS.GOLD }}>Sending</span>
                          </Box>
                        ) : dialogData.status ===
                          NotificationStatus.RETRYING ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <SyncIcon
                              sx={{ color: COLORS.WarningOrange, fontSize: 18 }}
                            />
                            <span style={{ color: COLORS.WarningOrange }}>
                              Retrying
                            </span>
                          </Box>
                        ) : dialogData.status ===
                          NotificationStatus.PARTIALLY_SENT ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <WarningAmberIcon
                              sx={{ color: COLORS.WarningOrange, fontSize: 18 }}
                            />
                            <span style={{ color: COLORS.WarningOrange }}>
                              Partially Sent
                            </span>
                          </Box>
                        ) : dialogData.status === NotificationStatus.SENT ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{ color: COLORS.successGreen, fontSize: 18 }}
                            />
                            <span style={{ color: COLORS.successGreen }}>
                              Sent
                            </span>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CancelIcon
                              sx={{ color: COLORS.errorRed, fontSize: 18 }}
                            />
                            <span style={{ color: COLORS.errorRed }}>
                              Failed
                            </span>
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <CustomLabel text="Sent Date" fontWeight={600} />
                        <Box sx={{ color: COLORS.typographyColor }}>
                          {formatDate(dialogData.sentDate)}
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <CustomLabel text="From Email" fontWeight={600} />
                        <Box
                          sx={{
                            color: COLORS.typographyColor,
                            wordBreak: "break-all",
                          }}
                        >
                          {dialogData.fromEmail || "-"}
                        </Box>
                      </Box>
                      <Box>
                        <CustomLabel text="To Email" fontWeight={600} />
                        <Box
                          sx={{
                            color: COLORS.typographyColor,
                            wordBreak: "break-all",
                          }}
                        >
                          {dialogData.sendToEmail || "-"}
                        </Box>
                      </Box>
                      <Box>
                        <CustomLabel text="CC" fontWeight={600} />
                        <Box
                          sx={{
                            color: COLORS.typographyColor,
                            wordBreak: "break-all",
                          }}
                        >
                          {dialogData.ccEmail || "-"}
                        </Box>
                      </Box>
                    </Box>

                    <Box>
                      <CustomLabel text="Subject" fontWeight={600} />
                      <Box
                        sx={{
                          color: COLORS.typographyColor,
                          padding: 1,
                          backgroundColor: COLORS.lightGrayBackground,
                          borderRadius: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {dialogData.subject || "-"}
                      </Box>
                    </Box>

                    <Box>
                      <CustomLabel text="Body" fontWeight={600} />
                      <SafeHtml
                        html={dialogData.body}
                        sx={{
                          color: COLORS.typographyColor,
                          padding: 2,
                          backgroundColor: COLORS.lightGrayBackground,
                          borderRadius: 1,
                          maxHeight: "300px",
                          overflowY: "auto",
                          "& p": { margin: "0.5em 0" },
                        }}
                      />
                    </Box>
                  </Box>
                ),
              },
            ]
      }
      buttons={
        loading
          ? []
          : [
              ...(dialogData.attachmentId
                ? [
                    {
                      label: downloading
                        ? "Downloading..."
                        : "Download Attachment",
                      variant: "outlined" as const,
                      onClick: handleDownloadAttachment,
                      disabled:
                        downloading ||
                        !hasJwtPermission("DOWNLOAD_NOTIFICATION_ATTACHMENT"),
                    },
                  ]
                : []),
              ...(dialogData.detailsAttachmentId
                ? [
                    {
                      label: downloadingDetails
                        ? "Downloading..."
                        : "Download Details Attachment",
                      variant: "outlined" as const,
                      onClick: handleDownloadDetailsAttachment,
                      disabled:
                        downloadingDetails ||
                        !hasJwtPermission("DOWNLOAD_NOTIFICATION_ATTACHMENT"),
                    },
                  ]
                : []),
              ...(canResend
                ? [
                    {
                      label: resending ? "Resending..." : "Resend",
                      variant: "contained" as const,
                      bgColor: COLORS.GOLD,
                      onClick: handleResend,
                      disabled:
                        resending || !hasJwtPermission("RESEND_NOTIFICATION"),
                    },
                  ]
                : []),
            ]
      }
    />
  );
});

export default NotificationsPage;
